// src/components/SmartAlertButton.jsx
import React, { useState, useRef } from "react";
import { validateAndTriggerSmartAlert } from "../utils/smart_alert_button";

const ALERT_BEEP_SRC = "/assets/sounds/smart-alert-beep.ogg";

const SmartAlertButton = ({
  mode = "test",
  onFire = () => {},
  onSendStart = () => {},
  stealthMode = false,
  color,
}) => {
  const [firing, setFiring] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [statusText, setStatusText] = useState(null);
  const holdRef = useRef(null);

  const alertSoundsRef = useRef([
    new Audio(ALERT_BEEP_SRC),
    new Audio(ALERT_BEEP_SRC),
    new Audio(ALERT_BEEP_SRC),
  ]);

  const getTone = () => {
    // Error/blocked mode owns its own color and ignores the Test/Live switch color.
    if (mode === "error") {
      return {
        main: "#B91C1C",
        deep: "#7F1D1D",
        soft: "rgba(185,28,28,0.14)",
        glow: "rgba(185,28,28,0.28)",
        innerShadow: "rgba(127,29,29,0.42)",
      };
    }

    // Test and Live mode colors come from TestLiveModeSwitch.
    // Fallbacks are only used if the parent does not pass a color.
    if (mode === "live") {
      return {
        main: color || "#DC2626",
        deep: "#991B1B",
        soft: "rgba(220,38,38,0.14)",
        glow: "rgba(220,38,38,0.34)",
        innerShadow: "rgba(127,29,29,0.45)",
      };
    }

    if (mode === "test") {
      return {
        main: color || "#D97706",
        deep: "#92400E",
        soft: "rgba(217,119,6,0.14)",
        glow: "rgba(217,119,6,0.30)",
        innerShadow: "rgba(120,53,15,0.42)",
      };
    }

    return {
      main: color || "#64748B",
      deep: "#334155",
      soft: "rgba(100,116,139,0.12)",
      glow: "rgba(100,116,139,0.24)",
      innerShadow: "rgba(15,23,42,0.32)",
    };
  };

  const tone = getTone();

  const handleHoldStart = () => {
    if (firing || mode === "error") return;

    // Clear any prior send-result notice on the parent page before this new
    // countdown begins.
    onSendStart();

    let count = 3;
    setCountdown(count);
    setFiring(true);

    if (!stealthMode) {
      if ("vibrate" in navigator) navigator.vibrate([100]);
      const firstSound = alertSoundsRef.current[0];
      firstSound.currentTime = 0;
      firstSound.play().catch(() => {});
    }

    holdRef.current = setInterval(() => {
      count--;

      if (count > 0 && alertSoundsRef.current[3 - count]) {
        if (!stealthMode) {
          if ("vibrate" in navigator) navigator.vibrate([100]);
          const nextSound = alertSoundsRef.current[3 - count];
          nextSound.currentTime = 0;
          nextSound.play().catch(() => {});
        }
      }

      setCountdown(count);

      if (count === 0) {
        clearInterval(holdRef.current);
        holdRef.current = null;

        setCountdown(null);
        setFiring(false);
        setStatusText("SENDING...");

        // Location acquisition is intentionally owned by smart_alert_button.js.
        // That utility performs one authoritative on-demand location flow:
        // fresh browser/device -> trusted last-known browser/device -> coarse IP.
        //
        // Wait for the send flow to finish before starting the 4-second status
        // reset. Previously the reset timer started immediately and could clear
        // "Getting Location…" / "Sending…" before the request had completed.
        void (async () => {
          await validateAndTriggerSmartAlert({
            mode,
            onFire,
            setStatusText,
          });

          setTimeout(() => {
            setStatusText(null);
          }, 4000);
        })();
      }
    }, 1000);
  };

  const handleHoldEnd = () => {
    if (holdRef.current) {
      clearInterval(holdRef.current);
      holdRef.current = null;
    }

    setCountdown(null);
    setFiring(false);
  };

  const buttonLabel = statusText
    ? statusText
    : countdown !== null
    ? countdown
    : mode === "live"
    ? "ALERT"
    : mode === "test"
    ? "TEST"
    : "ERROR";

  const isLongLabel = String(buttonLabel).length > 7;

  return (
    <div style={styles.wrapper}>
      <div style={styles.statusRow}>
        <div
          style={{
            ...styles.status,
            backgroundColor: tone.main,
            boxShadow: `0 0 0 5px ${tone.soft}`,
          }}
        />
      </div>

      <div
        style={{
          ...styles.outerRing,
          borderColor: tone.main,
          background: `radial-gradient(circle at center, ${tone.soft} 0%, rgba(255,255,255,0.96) 62%, rgba(255,255,255,1) 100%)`,
          boxShadow: firing
            ? `0 0 0 8px ${tone.soft}, 0 0 30px 8px ${tone.glow}, inset 0 0 22px ${tone.glow}`
            : `0 12px 26px rgba(15,23,42,0.14), 0 0 0 8px ${tone.soft}, inset 0 0 14px rgba(15,23,42,0.06)`,
        }}
        onMouseDown={handleHoldStart}
        onMouseUp={handleHoldEnd}
        onMouseLeave={handleHoldEnd}
        onTouchStart={handleHoldStart}
        onTouchEnd={handleHoldEnd}
      >
        <div
          style={{
            ...styles.innerButton,
            ...(isLongLabel ? styles.innerButtonLongText : null),
            background: `linear-gradient(145deg, ${tone.main}, ${tone.deep})`,
            boxShadow: firing
              ? `0 0 0 10px ${tone.soft}, 0 18px 30px ${tone.innerShadow}, inset 0 1px 0 rgba(255,255,255,0.22)`
              : `0 14px 28px ${tone.innerShadow}, inset 0 1px 0 rgba(255,255,255,0.22)`,
            animation: firing ? "pulse 1s infinite" : "none",
          }}
        >
          {buttonLabel}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255,255,255,0.28);
          }
          70% {
            transform: scale(1.05);
            box-shadow: 0 0 0 18px rgba(255,255,255,0);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255,255,255,0);
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  // Page handles centering; responsive width keeps dot/ring aligned on mobile
  wrapper: {
    position: "relative",
    width: "min(260px, 100%)",
    maxWidth: 260,
    margin: "0 auto",
    padding: 0,
    boxSizing: "border-box",
  },
  statusRow: {
    height: 20,
    marginBottom: 16,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  status: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "2px solid #FFFFFF",
    boxSizing: "border-box",
    transition: "background-color 0.25s ease, box-shadow 0.25s ease",
  },
  outerRing: {
    width: "100%",
    aspectRatio: "1 / 1",
    borderRadius: "50%",
    border: "12px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    touchAction: "manipulation",
    transition:
      "box-shadow 0.3s ease, border-color 0.3s ease, background 0.3s ease",
    boxSizing: "border-box",
    margin: 0,
  },
  innerButton: {
    width: "73%",
    height: "73%",
    borderRadius: "50%",
    color: "#fff",
    fontSize: 30,
    fontWeight: 950,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    lineHeight: 1.05,
    padding: 14,
    boxSizing: "border-box",
    overflow: "hidden",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    userSelect: "none",
    letterSpacing: "0.4px",
    textShadow: "0 1px 2px rgba(0,0,0,0.22)",
    transition:
      "background 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease",
  },
  innerButtonLongText: {
    fontSize: 18,
    lineHeight: 1.08,
    letterSpacing: "-0.2px",
    padding: 18,
    textTransform: "uppercase",
  },
};

export default SmartAlertButton;
